import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Field } from "formik";
import Div from "@jumbo/shared/Div";
import { Label } from "app/pages/presentation/module/form/types";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import { EET_REZIMY, EET_PROSTREDIA } from "app/utils/constants/settings";
import { useSnackbar } from "notistack";
import jwtAuthAxios from "app/services/auth/jwtAuth";
import { getBEUrlForAdmin } from "app/services/api/management";

/** Dátum → DD.MM.RRRR (podľa pravidiel ekosystému). */
const formatDateSk = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}.${mm}.${d.getFullYear()}`;
};

const EetEkasa = ({ device, setFieldValue, values, t }) => {
  const { enqueueSnackbar } = useSnackbar();
  const eet = values?.customer?.devices?.[device]?.ekasa?.eet || {};

  const beDomain =
    values?.customer?.beDomain ??
    values?.customer?.dsokSettings?.customer?.beDomain;
  const customerId =
    values?.customer?.id ??
    values?.customer?._id ??
    values?.customer?.customerId;

  const [certificates, setCertificates] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState("");
  const [label, setLabel] = useState("");

  const baseUrl = () => `${getBEUrlForAdmin(beDomain)}/`;

  const fetchCertificates = useCallback(async () => {
    if (!customerId) return;
    setLoadingList(true);
    try {
      jwtAuthAxios.defaults.baseURL = baseUrl();
      const { data } = await jwtAuthAxios.get("/dsok/eet/certificates", {
        params: { customerId },
      });
      setCertificates(Array.isArray(data?.certificates) ? data.certificates : []);
    } catch (error) {
      enqueueSnackbar(t("pages.dsokDetails.eet.certLoadFailed"), {
        variant: "error",
      });
    } finally {
      setLoadingList(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, beDomain]);

  useEffect(() => {
    fetchCertificates();
  }, [fetchCertificates]);

  const handleUpload = async () => {
    if (!file) {
      enqueueSnackbar(t("pages.dsokDetails.eet.certPickFile"), {
        variant: "warning",
      });
      return;
    }
    if (!password) {
      enqueueSnackbar(t("pages.dsokDetails.eet.certPasswordRequired"), {
        variant: "warning",
      });
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("certificate", file);
      form.append("password", password);
      if (label) form.append("label", label);
      if (customerId != null) form.append("customerId", customerId);

      jwtAuthAxios.defaults.baseURL = baseUrl();
      const { data } = await jwtAuthAxios.post(
        "/dsok/eet/certificates",
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const certRef = data?.certificate?.certRef;
      enqueueSnackbar(t("pages.dsokDetails.eet.certUploaded"), {
        variant: "success",
      });
      setFile(null);
      setPassword("");
      setLabel("");
      await fetchCertificates();
      if (certRef) {
        setFieldValue(
          `customer.devices.${device}.ekasa.eet.certRef`,
          certRef
        );
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        t("pages.dsokDetails.eet.certUploadFailed");
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (certRef) => {
    try {
      jwtAuthAxios.defaults.baseURL = baseUrl();
      await jwtAuthAxios.delete(`/dsok/eet/certificates/${certRef}`);
      enqueueSnackbar(t("pages.dsokDetails.eet.certDeleted"), {
        variant: "success",
      });
      if (eet.certRef === certRef) {
        setFieldValue(`customer.devices.${device}.ekasa.eet.certRef`, "");
      }
      await fetchCertificates();
    } catch (error) {
      enqueueSnackbar(t("pages.dsokDetails.eet.certDeleteFailed"), {
        variant: "error",
      });
    }
  };

  return (
    <>
      <Grid item xs={12} md={12} lg={12}>
        <Alert variant="outlined" severity="info">
          {t("pages.dsokDetails.eet.info")}
        </Alert>
      </Grid>

      {/* Identifikácia poplatníka / prevádzky */}
      <Grid item xs={12} md={4} lg={4}>
        <Div sx={{ mt: 1, mb: 1 }}>
          <JumboTextField
            fullWidth
            required
            name={`customer.devices.${device}.ekasa.eet.eicPopl`}
            label={
              <Label
                text={t("pages.dsokDetails.eet.eicPopl")}
                helpText={t("pages.dsokDetails.eet.eicPoplHelp")}
                required
              />
            }
          />
        </Div>
      </Grid>
      <Grid item xs={12} md={4} lg={4}>
        <Div sx={{ mt: 1, mb: 1 }}>
          <JumboTextField
            fullWidth
            name={`customer.devices.${device}.ekasa.eet.eicPoverujiciho`}
            label={
              <Label
                text={t("pages.dsokDetails.eet.eicPoverujiciho")}
                helpText={t("pages.dsokDetails.eet.eicPoverujicihoHelp")}
              />
            }
          />
        </Div>
      </Grid>
      <Grid item xs={12} md={4} lg={4}>
        <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
          <FormControlLabel
            control={
              <Field
                name={`customer.devices.${device}.ekasa.eet.povereniVicePopl`}
                component={Switch}
                onChange={(e) =>
                  setFieldValue(
                    `customer.devices.${device}.ekasa.eet.povereniVicePopl`,
                    e.target.checked
                  )
                }
              />
            }
            label={t("pages.dsokDetails.eet.povereniVicePopl")}
            checked={eet.povereniVicePopl === true}
          />
        </Div>
      </Grid>

      <Grid item xs={12} md={4} lg={4}>
        <Div sx={{ mt: 1, mb: 1 }}>
          <JumboTextField
            fullWidth
            required
            name={`customer.devices.${device}.ekasa.eet.idJednotky`}
            label={
              <Label
                text={t("pages.dsokDetails.eet.idJednotky")}
                helpText={t("pages.dsokDetails.eet.idJednotkyHelp")}
                required
              />
            }
          />
        </Div>
      </Grid>
      <Grid item xs={12} md={4} lg={4}>
        <Div sx={{ mt: 1, mb: 1 }}>
          <JumboTextField
            fullWidth
            required
            name={`customer.devices.${device}.ekasa.eet.idPokl`}
            label={
              <Label
                text={t("pages.dsokDetails.eet.idPokl")}
                helpText={t("pages.dsokDetails.eet.idPoklHelp")}
                required
              />
            }
          />
        </Div>
      </Grid>
      <Grid item xs={12} md={12} lg={12}></Grid>

      {/* Režim a prostredie */}
      <Grid item xs={12} md={4} lg={4}>
        <Div sx={{ mt: 1, mb: 1 }}>
          <JumboTextField
            fullWidth
            required
            select
            InputLabelProps={{ required: false }}
            name={`customer.devices.${device}.ekasa.eet.rezim`}
            label={
              <Label
                text={t("pages.dsokDetails.eet.rezim")}
                helpText={t("pages.dsokDetails.eet.rezimHelp")}
                required
              />
            }
          >
            {EET_REZIMY.map(({ code, name }) => (
              <MenuItem key={code} value={code}>
                {t(`pages.dsokDetails.eet.${name}`)}
              </MenuItem>
            ))}
          </JumboTextField>
        </Div>
      </Grid>
      <Grid item xs={12} md={4} lg={4}>
        <Div sx={{ mt: 1, mb: 1 }}>
          <JumboTextField
            fullWidth
            required
            select
            InputLabelProps={{ required: false }}
            name={`customer.devices.${device}.ekasa.eet.prostredi`}
            label={
              <Label
                text={t("pages.dsokDetails.eet.prostredi")}
                helpText={t("pages.dsokDetails.eet.prostrediHelp")}
                required
              />
            }
          >
            {EET_PROSTREDIA.map(({ code, name }) => (
              <MenuItem key={code} value={code}>
                {t(`pages.dsokDetails.eet.${name}`)}
              </MenuItem>
            ))}
          </JumboTextField>
        </Div>
      </Grid>
      <Grid item xs={12} md={12} lg={12}></Grid>

      {/* Certifikát */}
      <Grid item xs={12} md={12} lg={12}>
        <Typography variant="subtitle2" sx={{ mt: 1 }}>
          {t("pages.dsokDetails.eet.certificate")}
        </Typography>
      </Grid>
      <Grid item xs={12} md={6} lg={6}>
        <Div sx={{ mt: 1, mb: 1, display: "flex", alignItems: "center" }}>
          <JumboTextField
            fullWidth
            required
            select
            InputLabelProps={{ required: false }}
            name={`customer.devices.${device}.ekasa.eet.certRef`}
            label={
              <Label text={t("pages.dsokDetails.eet.certRef")} required />
            }
          >
            {certificates.length === 0 && (
              <MenuItem value="" disabled>
                {t("pages.dsokDetails.eet.certNone")}
              </MenuItem>
            )}
            {certificates.map((c) => (
              <MenuItem key={c.certRef} value={c.certRef}>
                {(c.label || c.certRef) +
                  (c.notAfter ? ` — ${formatDateSk(c.notAfter)}` : "")}
              </MenuItem>
            ))}
          </JumboTextField>
          <Tooltip title={t("buttons.refresh")}>
            <span>
              <IconButton
                size="small"
                onClick={fetchCertificates}
                disabled={loadingList}
                sx={{ ml: 0.5 }}
              >
                {loadingList ? <CircularProgress size={18} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
          {eet.certRef && (
            <Tooltip title={t("buttons.delete")}>
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDelete(eet.certRef)}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Div>
      </Grid>

      {/* Upload nového certifikátu */}
      <Grid item xs={12} md={12} lg={12}>
        <Typography variant="caption" color="text.secondary">
          {t("pages.dsokDetails.eet.certUploadHint")}
        </Typography>
      </Grid>
      <Grid item xs={12} md={4} lg={4}>
        <Div sx={{ mt: 1, mb: 1 }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFileIcon />}
            fullWidth
          >
            {file ? file.name : t("pages.dsokDetails.eet.certPickFile")}
            <input
              hidden
              type="file"
              accept=".p12,.pfx,application/x-pkcs12"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </Button>
        </Div>
      </Grid>
      <Grid item xs={12} md={3} lg={3}>
        <Div sx={{ mt: 1, mb: 1 }}>
          <TextField
            fullWidth
            size="small"
            type="password"
            autoComplete="new-password"
            label={t("pages.dsokDetails.eet.certPassword")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Div>
      </Grid>
      <Grid item xs={12} md={3} lg={3}>
        <Div sx={{ mt: 1, mb: 1 }}>
          <TextField
            fullWidth
            size="small"
            label={t("pages.dsokDetails.eet.certLabel")}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </Div>
      </Grid>
      <Grid item xs={12} md={2} lg={2}>
        <Div sx={{ mt: 1, mb: 1 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleUpload}
            disabled={uploading}
            sx={{ boxShadow: "none", height: "40px" }}
          >
            {uploading ? (
              <CircularProgress size={20} />
            ) : (
              t("pages.dsokDetails.eet.certUpload")
            )}
          </Button>
        </Div>
      </Grid>
    </>
  );
};

export default EetEkasa;
